
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { blogPosts } from '@/data/blog';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Clock, ZoomIn } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [imageOpen, setImageOpen] = useState(false);
  
  // Find the post with the matching slug
  const post = useMemo(() => blogPosts.find(post => post.slug === slug), [slug]);
  
  // Get the next and previous posts
  const { nextPost, prevPost } = useMemo(() => {
    if (!post) return { nextPost: null, prevPost: null };
    
    const currentIndex = blogPosts.findIndex(p => p.slug === slug);
    return {
      nextPost: currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null,
      prevPost: currentIndex > 0 ? blogPosts[currentIndex - 1] : null
    };
  }, [slug, post]);
  
  // Redirect if post not found
  useEffect(() => {
    if (!post && slug) {
      navigate('/404');
    }
  }, [post, slug, navigate]);
  
  // Fixed the renderContent function to properly handle different content types with correct TypeScript typing
  const renderContent = () => {
    if (!post || !post.content) return null;
    
    // Check if the content is a string (HTML content)
    if (typeof post.content === 'string') {
      return <div className="space-y-8" dangerouslySetInnerHTML={{ __html: post.content }} />;
    }
    
    // Check if the content is an array of paragraphs - explicitly cast to string array when it's an array
    if (Array.isArray(post.content)) {
      const paragraphs = post.content as string[];
      return (
        <div className="space-y-8">
          {paragraphs.map((paragraph: string, index: number) => (
            <p key={index} className="mb-4">{paragraph}</p>
          ))}
        </div>
      );
    }
    
    // Default case if content is in an unexpected format
    return null;
  };

  if (!post) return null;
  
  return (
    <Layout>
      <article className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Back button */}
            <Button asChild variant="outline" className="mb-8">
              <Link to="/blog" className="inline-flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbage til blog
              </Link>
            </Button>
            
            {/* Featured Image with zoom dialog */}
            {post.image && (
              <div className="mb-8 rounded-lg overflow-hidden shadow-md relative group">
                <Dialog open={imageOpen} onOpenChange={setImageOpen}>
                  <DialogTrigger asChild>
                    <div className="cursor-pointer relative">
                      <img 
                        src={post.image} 
                        alt={post.title} 
                        className="w-full h-auto object-cover"
                        loading="eager"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100">
                        <ZoomIn className="text-white h-10 w-10" />
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full h-auto object-contain"
                    />
                  </DialogContent>
                </Dialog>
              </div>
            )}
            
            {/* Header */}
            <header className="mb-12">
              <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                {post.title}
              </h1>
              
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <Calendar className="h-4 w-4 mr-1" />
                <span className="mr-4">{post.date}</span>
                <Clock className="h-4 w-4 mr-1" />
                <span>{post.readTime} læsning</span>
              </div>

              <div className="mb-4 inline-block">
                <span className="text-xs font-medium px-3 py-1 bg-gray-100 rounded-full text-gray-600">
                  {post.category}
                </span>
              </div>
            </header>
            
            {/* Content with updated styling for subheadings */}
            <div className="prose prose-lg max-w-none mb-16 prose-headings:font-serif prose-h3:text-2xl prose-h3:font-bold prose-h3:mb-6">
              {renderContent()}
            </div>
            
            {/* Post navigation */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                {prevPost ? (
                  <Button asChild variant="outline" className="flex-1">
                    <Link to={`/blog/${prevPost.slug}`} className="text-left">
                      <span className="block text-sm text-gray-500 mb-1">Forrige indlæg</span>
                      <span className="font-medium truncate">{prevPost.title}</span>
                    </Link>
                  </Button>
                ) : <div className="flex-1" />}
                
                {nextPost && (
                  <Button asChild variant="outline" className="flex-1">
                    <Link to={`/blog/${nextPost.slug}`} className="text-right">
                      <span className="block text-sm text-gray-500 mb-1">Næste indlæg</span>
                      <span className="font-medium truncate">{nextPost.title}</span>
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
};

export default BlogPost;
